/**
 * Browser screenshot + screen recording capture.
 *
 * Two distinct mechanisms, two distinct UX contracts:
 *
 *   captureScreenshot()    — Rasterizes the host page's DOM via SVG
 *                            foreignObject (modern-screenshot). NO permission
 *                            prompt. Captures only what's already in the
 *                            current page's DOM (not browser chrome, not
 *                            other tabs). Works on mobile.
 *
 *   startScreenRecording() — Uses getDisplayMedia() + MediaRecorder. Browser
 *                            shows the native screen-share picker — the user
 *                            picks which screen/window/tab to share over time.
 *                            Desktop only.
 */

import { domToCanvas } from 'modern-screenshot';

// Injected at build time by build.mjs (see WIDGET_MAX_RECORDING_MS define).
// Default: 300_000 ms (5 min). Override via env at build time.
declare const WIDGET_MAX_RECORDING_MS: number;
const MAX_RECORDING_MS = WIDGET_MAX_RECORDING_MS;
const MAX_RECORDING_BYTES = 25 * 1024 * 1024;

export type CaptureResult =
  | { ok: true; blob: Blob; mimeType: string }
  | { ok: false; reason: 'unsupported' | 'denied' | 'failed'; message: string };

/** Screenshot is supported anywhere DOM exists — i.e., every browser, including mobile. */
export function isScreenshotSupported(): boolean {
  return typeof document !== 'undefined' && !!document.documentElement;
}

/** Recording requires getDisplayMedia (desktop browsers only). */
export function isRecordingSupported(): boolean {
  return typeof navigator !== 'undefined'
    && !!navigator.mediaDevices
    && typeof navigator.mediaDevices.getDisplayMedia === 'function'
    && !(typeof matchMedia === 'function' && matchMedia('(pointer: coarse)').matches);
}

/** @deprecated Use isScreenshotSupported() / isRecordingSupported() — kept for API compatibility. */
export const isCaptureSupported = isRecordingSupported;

/**
 * Capture the current page's DOM as a PNG. No permission prompt — pure DOM
 * rasterization via SVG foreignObject. The widget host element is excluded
 * from the capture so the launcher/modal doesn't show up in the screenshot.
 */
// Transparent 1×1 PNG — substituted for any image that can't be fetched
// within the timeout window so the rasterizer doesn't stall.
const FETCH_PLACEHOLDER = 'data:image/png;base64,iVBORw0KGgoAAAANSUhEUgAAAAEAAAABCAYAAAAfFcSJAAAAC0lEQVQI12NgAAIABQAABjE+ibYAAAAASUVORK5CYII=';

// Attribute we tag onto scrolled containers in the LIVE DOM right before
// capture so that modern-screenshot's onCloneNode hook can recognize them
// in the freshly-cloned tree (clones don't carry references to originals).
// Stripped from the live DOM in a finally block so the host page is never
// left with widget markers attached.
const SCROLL_MARKER = 'data-df-scroll-marker';

export async function captureScreenshot(): Promise<CaptureResult> {
  if (!isScreenshotSupported()) {
    return { ok: false, reason: 'unsupported', message: 'Screenshot not supported in this environment' };
  }

  // Map<marker-id, {scrollTop, scrollLeft}>. Built by walking the live DOM
  // for every element whose overflow-y / overflow-x is auto or scroll AND
  // whose scrollTop / scrollLeft is non-zero. We tag each such element with
  // SCROLL_MARKER, then read it back in onCloneNode.
  //
  // Why this is needed: window.scrollY / body.scrollTop are zero on modern
  // SPAs that scroll inside a nested container (e.g. <main overflow-auto>).
  // The original `marginTop = -scrollY` trick on body only fixes traditional
  // window-scrolling pages — for nested-scroll layouts, the screenshot
  // always shows the top of the page regardless of how far the user has
  // scrolled. This walk + per-container transform fixes that.
  const scrollContainers = new Map<string, { scrollTop: number; scrollLeft: number }>();
  const cleanupMarkers: HTMLElement[] = [];

  const recordScrollContainers = (root: Element) => {
    const stack: Element[] = [root];
    while (stack.length) {
      const el = stack.pop()!;
      if (el instanceof HTMLElement && el.id !== 'klientele-widget-host') {
        const cs = getComputedStyle(el);
        const scrollableY = (cs.overflowY === 'auto' || cs.overflowY === 'scroll') && el.scrollTop > 0;
        const scrollableX = (cs.overflowX === 'auto' || cs.overflowX === 'scroll') && el.scrollLeft > 0;
        if (scrollableY || scrollableX) {
          const id = `df-sc-${scrollContainers.size}`;
          el.setAttribute(SCROLL_MARKER, id);
          cleanupMarkers.push(el);
          scrollContainers.set(id, {
            scrollTop: Math.round(el.scrollTop),
            scrollLeft: Math.round(el.scrollLeft),
          });
        }
      }
      for (const child of Array.from(el.children)) stack.push(child);
    }
  };

  try {
    const dpr = Math.min(window.devicePixelRatio || 1, 2);
    const vw = window.innerWidth;
    const vh = window.innerHeight;
    const scrollX = Math.round(window.scrollX || window.pageXOffset || 0);
    const scrollY = Math.round(window.scrollY || window.pageYOffset || 0);

    recordScrollContainers(document.documentElement);

    // Strategy: render a viewport-sized canvas (width=vw, height=vh) so the SVG
    // foreignObject clips at exactly what the user sees.
    //
    // Two complementary shift mechanisms:
    //   (a) Window/body scroll — handled by marginTop on the cloned <body>.
    //       Works for traditional pages where the document scrolls.
    //   (b) Nested-container scroll — handled by per-container CSS transform
    //       applied via the SCROLL_MARKER lookup. Necessary for modern SPAs
    //       (incl. the Klientele dashboard) that scroll inside <main> rather
    //       than the window.
    //
    // position:fixed elements render at their CSS position relative to the
    // foreignObject (y=0), so headers/toolbars appear correctly regardless
    // of scroll depth.
    //
    // Speed strategy — all DOM screenshot libraries serialize the full document and
    // fetch external resources (fonts, images). On complex pages this can take 30s+.
    // We cut that to <500ms by:
    //   1. Stripping external <link rel="stylesheet"> from the clone — eliminates
    //      the font-loading cascade (Google Fonts etc). Computed styles are already
    //      applied by the browser (CSSOM) so layout/colors/borders stay correct;
    //      only web fonts fall back to system fonts, which is fine for bug reports.
    //   2. Stripping <script> and <noscript> — no JS re-execution risk in SVG.
    //   3. fetch.timeout: 300ms — any remaining external resource (inline images,
    //      custom @font-face on same origin) fails fast and gets a transparent placeholder.
    const canvas = await domToCanvas(document.documentElement, {
      width: vw,
      height: vh,
      scale: dpr,
      filter: (node: Node) => {
        if (!(node instanceof HTMLElement)) return true;
        if (node.id === 'klientele-widget-host') return false;
        const tag = node.tagName;
        // Skip external stylesheets — biggest source of slowness (font cascade).
        // Computed styles remain applied so visual output is still accurate.
        if (tag === 'LINK') {
          const rel = (node.getAttribute('rel') ?? '').toLowerCase();
          const href = node.getAttribute('href') ?? '';
          if (rel.includes('stylesheet') && href && !href.startsWith(location.origin) && !href.startsWith('/')) return false;
        }
        // Skip scripts and noscript — no effect on visual rendering in SVG context.
        if (tag === 'SCRIPT' || tag === 'NOSCRIPT') return false;
        return true;
      },
      onCloneNode: (clonedNode: Node) => {
        if (!(clonedNode instanceof HTMLElement)) return;

        // Traditional window/body scrolling.
        if (clonedNode.tagName === 'BODY') {
          if (scrollY > 0) clonedNode.style.marginTop = `-${scrollY}px`;
          if (scrollX > 0) clonedNode.style.marginLeft = `-${scrollX}px`;
          clonedNode.style.overflow = 'visible';
          return;
        }

        // Nested-container scrolling. The marker was placed on the LIVE
        // element; modern-screenshot copies attributes through to the
        // clone, so we can look it up by attribute on the cloned node.
        const markerId = clonedNode.getAttribute(SCROLL_MARKER);
        if (markerId) {
          const sc = scrollContainers.get(markerId);
          if (sc) {
            // Disable clipping on the cloned container so children
            // translated outside its box still render into the SVG.
            // The outer foreignObject (vw × vh) does the final crop.
            clonedNode.style.overflow = 'visible';
            // Shift every child up/left by the recorded scroll offset.
            // Using transform on each child preserves layout-flow stacking
            // (children stay in their relative positions) while visually
            // moving them so the currently-scrolled-into-view content
            // lands where the user sees it.
            for (const child of Array.from(clonedNode.children)) {
              if (child instanceof HTMLElement) {
                child.style.transform = `translate(${-sc.scrollLeft}px, ${-sc.scrollTop}px)`;
                child.style.transformOrigin = 'top left';
              }
            }
          }
          // Strip the marker from the clone so it doesn't leak into the
          // serialized SVG. (Cleanup of the LIVE DOM happens in finally.)
          clonedNode.removeAttribute(SCROLL_MARKER);
        }
      },
      // `timeout` exists at runtime in modern-screenshot but isn't in the
      // exported types — cast to keep the perf escape valve for slow
      // external image / font requests during DOM rasterization.
      fetch: {
        requestInit: { cache: 'force-cache' },
        timeout: 300,
        placeholderImage: FETCH_PLACEHOLDER,
      } as never,
    });

    const blob = await new Promise<Blob | null>((resolve) => canvas.toBlob(resolve, 'image/png'));
    if (!blob) throw new Error('Screenshot rasterization returned null');
    return { ok: true, blob, mimeType: 'image/png' };
  } catch (err) {
    const message = err instanceof Error ? err.message : String(err);
    return { ok: false, reason: 'failed', message };
  } finally {
    // Always strip markers from the live DOM, even on failure.
    for (const el of cleanupMarkers) el.removeAttribute(SCROLL_MARKER);
  }
}

export interface RecordingHandle {
  /** Promise resolves when the recording stops (manually or at maxDuration). */
  result: Promise<CaptureResult>;
  /** Stops the recording immediately. The `result` promise resolves shortly after. */
  stop: () => void;
  /** Live ms elapsed since recording started. Polled by the UI for the timer. */
  getElapsedMs: () => number;
}

/**
 * Start a screen recording with audio (microphone + optional system audio).
 *
 * Runs inline in the embedding page. getDisplayMedia shows the browser's
 * native screen-share picker. Mic is best-effort — denial is non-fatal.
 * Returns a handle the UI can poll for elapsed time and stop manually.
 * Auto-stops at MAX_RECORDING_MS or MAX_RECORDING_BYTES.
 */
export async function startScreenRecording(): Promise<RecordingHandle> {
  if (!isRecordingSupported()) {
    return {
      result: Promise.resolve({ ok: false, reason: 'unsupported', message: 'Screen recording not supported on this browser' }),
      stop: () => {},
      getElapsedMs: () => 0,
    };
  }

  // getDisplayMedia must fire synchronously within the user-gesture window.
  // Omit audio:true — it triggers microphone/camera Permissions-Policy
  // violations on pages that restrict those features.
  let displayStream: MediaStream;
  try {
    displayStream = await navigator.mediaDevices.getDisplayMedia({ video: true, audio: true });
  } catch (err) {
    const message = err instanceof Error ? err.message : String(err);
    const denied = /permission|denied|aborted|notallowed/i.test(message);
    return {
      result: Promise.resolve({ ok: false, reason: denied ? 'denied' : 'failed', message: denied ? 'Screen sharing was cancelled' : message }),
      stop: () => {},
      getElapsedMs: () => 0,
    };
  }

  // Mic — best-effort. Query permissions BEFORE calling getUserMedia so we never
  // log a Permissions-Policy violation on pages that deny microphone.
  let micStream: MediaStream | null = null;
  let micAllowed = true;
  if (navigator.permissions) {
    try {
      const status = await navigator.permissions.query({ name: 'microphone' as PermissionName });
      micAllowed = status.state !== 'denied';
    } catch { /* permissions API unavailable */ }
  }
  if (micAllowed) {
    try {
      micStream = await navigator.mediaDevices.getUserMedia({
        audio: { echoCancellation: true, noiseSuppression: true, autoGainControl: true },
        video: false,
      });
    } catch { /* mic denied or unavailable — continue video-only */ }
  }

  const stream = new MediaStream();
  displayStream.getVideoTracks().forEach((t) => stream.addTrack(t));

  // Mix display audio (tab/system) + mic into a single audio track.
  const displayAudioTracks = displayStream.getAudioTracks();
  const micAudioTracks = micStream?.getAudioTracks() ?? [];
  const allAudioTracks = [...displayAudioTracks, ...micAudioTracks];

  if (allAudioTracks.length > 0) {
    try {
      const Ctx = ((window as unknown as Record<string, unknown>).AudioContext ?? (window as unknown as Record<string, unknown>).webkitAudioContext) as typeof AudioContext;
      const audioCtx = new Ctx();
      const dest = audioCtx.createMediaStreamDestination();
      allAudioTracks.forEach((t) => audioCtx.createMediaStreamSource(new MediaStream([t])).connect(dest));
      dest.stream.getAudioTracks().forEach((t) => stream.addTrack(t));
    } catch {
      allAudioTracks.forEach((t) => stream.addTrack(t));
    }
  }

  const mimeType = ['video/webm;codecs=vp9', 'video/webm;codecs=vp8', 'video/webm'].find((m) => MediaRecorder.isTypeSupported(m)) ?? '';
  const recorder = new MediaRecorder(stream, mimeType ? { mimeType } : undefined);
  const chunks: Blob[] = [];
  let totalBytes = 0;
  const startedAt = Date.now();

  recorder.ondataavailable = (e) => {
    if (e.data && e.data.size > 0) {
      chunks.push(e.data);
      totalBytes += e.data.size;
      if (totalBytes >= MAX_RECORDING_BYTES) {
        try { recorder.stop(); } catch { /* already stopping */ }
      }
    }
  };

  stream.getVideoTracks()[0]?.addEventListener('ended', () => {
    if (recorder.state !== 'inactive') try { recorder.stop(); } catch { /* noop */ }
  });

  const timeout = setTimeout(() => {
    if (recorder.state !== 'inactive') try { recorder.stop(); } catch { /* noop */ }
  }, MAX_RECORDING_MS);

  const cleanup = () => {
    stream.getTracks().forEach((t) => t.stop());
    displayStream.getTracks().forEach((t) => t.stop());
    micStream?.getTracks().forEach((t) => t.stop());
  };

  const result = new Promise<CaptureResult>((resolve) => {
    recorder.onstop = () => {
      clearTimeout(timeout);
      cleanup();
      const blob = new Blob(chunks, { type: mimeType || 'video/webm' });
      resolve({ ok: true, blob, mimeType: blob.type });
    };
    recorder.onerror = (e) => {
      clearTimeout(timeout);
      cleanup();
      resolve({ ok: false, reason: 'failed', message: `Recorder error: ${(e as ErrorEvent).message ?? 'unknown'}` });
    };
  });

  recorder.start(1000);

  return {
    result,
    stop: () => { if (recorder.state !== 'inactive') try { recorder.stop(); } catch { /* noop */ } },
    getElapsedMs: () => Date.now() - startedAt,
  };
}

export const RECORDING_LIMITS = { maxMs: MAX_RECORDING_MS, maxBytes: MAX_RECORDING_BYTES };
